import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Input, Label, PasswordField, Stepper } from '@nx/ui';
import { useI18n } from '@nx/i18n';
import type { PartnerRole } from '@nx/domain';
import { isValidEmail } from '@nx/shared';
import { PARTNER_ROLE_DISPLAY } from '../constants/partnerRoleDisplay';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { authCleared, registerRequested } from '../store/slices/authSlice';

interface FormState {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  companyName: string;
  partnerRole: PartnerRole | null;
}

const initialState: FormState = {
  fullName: '',
  email: '',
  password: '',
  confirmPassword: '',
  phone: '',
  companyName: '',
  partnerRole: null,
};

export function Signup() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { isAuthenticating, authResult, identity } = useAppSelector((s) => s.auth);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(initialState);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(authCleared());
  }, [dispatch]);

  useEffect(() => {
    if (identity) {
      navigate(identity.emailVerified ? '/dashboard' : '/verify-email', { replace: true });
    }
  }, [identity, navigate]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateStep = (target: number): string | null => {
    if (target === 1) {
      if (!form.fullName.trim()) return 'Please enter your full name.';
      if (!form.phone.trim()) return 'Please enter your phone number.';
      if (!form.email.trim()) return 'Please enter your email address.';
      if (!isValidEmail(form.email)) return 'Please enter a valid email address.';
      if (!form.password) return 'Please enter a password.';
      if (form.password.length < 6) return 'Password must contain at least 6 characters.';
      if (form.password !== form.confirmPassword) return 'Passwords do not match.';
    }
    if (target === 2) {
      if (!form.companyName.trim()) return 'Please enter your company name.';
      if (!form.partnerRole) return 'Please select a company role.';
    }
    return null;
  };

  const handleNext = () => {
    const error = validateStep(step);
    if (error) {
      setLocalError(error);
      return;
    }
    setLocalError(null);
    setStep((prev) => Math.min(prev + 1, 3));
  };

  const handleBack = () => {
    setLocalError(null);
    if (step === 1) {
      navigate('/login');
      return;
    }
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = () => {
    const step2Error = validateStep(2);
    if (step2Error) {
      setLocalError(step2Error);
      setStep(2);
      return;
    }
    setLocalError(null);
    dispatch(
      registerRequested({
        email: form.email.trim(),
        password: form.password,
        user: { fullName: form.fullName.trim(), phone: form.phone.trim() },
        company: {
          companyName: form.companyName.trim(),
          partnerRole: form.partnerRole as PartnerRole,
        },
      }),
    );
  };

  const selectedLabelKey = PARTNER_ROLE_DISPLAY.find(
    (opt) => opt.value === form.partnerRole,
  )?.signupTitleKey;

  const steps = [
    { id: 1, title: t('signup.step1Title') },
    { id: 2, title: t('signup.step2Title') },
    { id: 3, title: t('signup.step3Title') },
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F5] px-4 py-10 md:py-14">
      <div className="mx-auto w-full max-w-2xl">
        <div className="mb-8 text-center">
          <h1
            className="text-3xl md:text-4xl text-[#3D3328] font-bold"
            style={{ fontFamily: 'var(--font-sans)' }}
          >
            {t('signup.title')}
          </h1>
          <p className="mt-2 text-sm text-[#78665A]">{t('signup.subtitle')}</p>
        </div>

        {(localError || authResult.error) && (
          <Alert variant="destructive" title={t('signup.failed')} className="mb-6">
            {localError ?? authResult.message}
          </Alert>
        )}

        <Stepper steps={steps} currentStep={step} />

        <div className="mt-8 rounded-2xl border border-[#3D3328]/10 bg-white p-6 shadow-lg md:p-8">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl text-[#3D3328] font-semibold">
                  {t('signup.step1Title')}
                </h2>
                <p className="mt-1 text-sm text-[#78665A]">{t('signup.step1Desc')}</p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label>{t('common.fullName')} *</Label>
                  <Input
                    value={form.fullName}
                    onChange={(e) => setField('fullName', e.target.value)}
                    disabled={isAuthenticating}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('common.phone')} *</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    disabled={isAuthenticating}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('common.email')} *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    autoComplete="email"
                    disabled={isAuthenticating}
                  />
                </div>
                <PasswordField
                  label={`${t('common.password')} *`}
                  value={form.password}
                  onChange={(e) => setField('password', e.target.value)}
                  autoComplete="new-password"
                  disabled={isAuthenticating}
                />
                <PasswordField
                  label={`${t('common.confirmPassword')} *`}
                  value={form.confirmPassword}
                  onChange={(e) => setField('confirmPassword', e.target.value)}
                  autoComplete="new-password"
                  disabled={isAuthenticating}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl text-[#3D3328] font-semibold">
                  {t('signup.step2Title')}
                </h2>
                <p className="mt-1 text-sm text-[#78665A]">{t('signup.step2Desc')}</p>
              </div>
              <div className="space-y-2">
                <Label>{t('common.companyName')} *</Label>
                <Input
                  value={form.companyName}
                  onChange={(e) => setField('companyName', e.target.value)}
                  disabled={isAuthenticating}
                />
              </div>
              <div className="space-y-3">
                <Label>{t('signup.companyRole')} *</Label>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {PARTNER_ROLE_DISPLAY.map((option) => {
                    const selected = form.partnerRole === option.value;
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setField('partnerRole', option.value)}
                        className={`flex items-start gap-4 rounded-xl border p-4 text-left transition ${
                          selected
                            ? 'border-[#1A1A1A] bg-[#1A1A1A] text-[#F4D03F]'
                            : 'border-[#3D3328]/15 hover:border-[#1A1A1A]/40'
                        }`}
                      >
                        <div
                          className={`mt-0.5 shrink-0 rounded-lg p-2 ${
                            selected ? 'bg-[#F4D03F]/15' : 'bg-[#3D3328]/5'
                          }`}
                        >
                          <Icon
                            className={`h-5 w-5 ${
                              selected ? 'text-[#F4D03F]' : 'text-[#3D3328]'
                            }`}
                          />
                        </div>
                        <div>
                          <div className="text-base font-semibold">{t(option.signupTitleKey)}</div>
                          <div
                            className={`mt-1 text-sm ${
                              selected ? 'text-[#FAF8F5]' : 'text-[#78665A]'
                            }`}
                          >
                            {t(option.signupDescKey)}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-2xl text-[#3D3328] font-semibold">
                {t('signup.step3Title')}
              </h2>
              <p className="text-sm text-[#78665A]">{t('signup.step3Desc')}</p>
              <div className="space-y-2 rounded-xl border border-[#3D3328]/10 bg-[#FAF8F5] p-4 text-sm text-[#3D3328]">
                <SummaryRow label={t('common.fullName')} value={form.fullName} />
                <SummaryRow label={t('common.email')} value={form.email} />
                <SummaryRow label={t('common.phone')} value={form.phone} />
                <SummaryRow label={t('common.companyName')} value={form.companyName} />
                <SummaryRow
                  label={t('signup.companyRole')}
                  value={selectedLabelKey ? t(selectedLabelKey) : '-'}
                />
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-col gap-3 border-t border-[#3D3328]/10 pt-6 md:flex-row md:items-center md:justify-between">
            <Button variant="outline" onClick={handleBack} disabled={isAuthenticating}>
              {step === 1 ? t('common.back') : t('common.back')}
            </Button>
            {step < 3 ? (
              <Button onClick={handleNext} disabled={isAuthenticating}>
                {t('common.continue')}
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isAuthenticating}>
                {isAuthenticating ? t('signup.creating') : t('signup.create')}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-[#78665A]">
          {t('signup.haveAccount')}
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="ml-2 text-[#1A1A1A] font-semibold hover:underline"
          >
            {t('common.signIn')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="font-medium">{label}: </span>
      {value || '-'}
    </div>
  );
}
